import React, { useCallback, useEffect, useState } from "react";
import { api, toastError } from "../../lib/api";
import { useEffectiveRole, canAtLeast } from "../../lib/roles";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

interface CommissionSummary {
  total: number;
  unpaid: number;
  paid: number;
  non_commissionable: number;
}

interface User360Data {
  user: {
    id: string;
    name: string | null;
    email: string | null;
    tenant_role: string | null;
  };
  assigned_contacts: Array<{
    id: string;
    first_name: string | null;
    last_name: string | null;
    lifecycle_status: string | null;
    location: string | null;
  }>;
  converted_patients: Array<{
    id: string;
    patient_contact_id: string;
    patient_name: string | null;
    commissionable: boolean;
    commission_status: string;
    rule_snapshot: {
      rate_amount?: number;
      currency?: string;
    } | null;
    converted_at: string | null;
  }>;
  commission_eligibility: {
    eligible: boolean;
    rate_amount: number | null;
    currency: string | null;
    requires_completed_months: number | null;
    rule_id: string | null;
  };
  commission_summary: CommissionSummary;
  training_summary?: {
    assigned: number;
    acknowledged: number;
    pending: number;
  };
  training_records?: Array<{
    material_id: string;
    title: string;
    category: string;
    kind: string;
    due_at: string | null;
    material_version: number;
    requires_acknowledgment: boolean;
    status: "pending" | "acknowledged";
    typed_signature: string | null;
    acknowledged_at: string | null;
  }>;
}

interface RuleForm {
  rate_amount: string;
  currency: string;
  requires_completed_months: string;
  active: boolean;
}

const EMPTY_RULE: RuleForm = {
  rate_amount: "25",
  currency: "CAD",
  requires_completed_months: "3",
  active: true,
};

export default function User360Panel({ userId, managementOnly = false }: { userId: string; managementOnly?: boolean }) {
  const [data, setData] = useState<User360Data | null>(null);
  const [ruleForm, setRuleForm] = useState<RuleForm>(EMPTY_RULE);
  const [savingRule, setSavingRule] = useState(false);
  const role = useEffectiveRole();
  const canView = canAtLeast(role, "representative");
  const canManageCommission = role === "owner_admin";

  const load = useCallback(async () => {
    if (!userId || !canView) return;
    setData(null);
    try {
      const response = await api.get<User360Data>(`/tenant/users/${userId}/360`);
      setData(response.data);
      const eligibility = response.data.commission_eligibility;
      setRuleForm({
        rate_amount: String(eligibility.rate_amount ?? 25),
        currency: eligibility.currency ?? "CAD",
        requires_completed_months: String(eligibility.requires_completed_months ?? 3),
        active: eligibility.rule_id ? eligibility.eligible : true,
      });
    } catch (e) {
      toastError(e);
    }
  }, [userId, canView]);

  useEffect(() => {
    load();
  }, [load]);

  const saveCommissionRule = async () => {
    if (!data || !canManageCommission) return;
    const rateAmount = Number(ruleForm.rate_amount);
    const completedMonths = Number(ruleForm.requires_completed_months);
    if (!Number.isFinite(rateAmount) || rateAmount < 0 || !Number.isInteger(completedMonths) || completedMonths < 0) {
      toast.error("Enter a valid commission amount and completed-month requirement.");
      return;
    }

    setSavingRule(true);
    try {
      const payload = {
        user_id: userId,
        rate_amount: rateAmount,
        currency: ruleForm.currency.trim().toUpperCase() || "CAD",
        requires_completed_months: completedMonths,
        active: ruleForm.active,
      };
      if (data.commission_eligibility.rule_id) {
        await api.patch(`/tenant/commission-rules/${data.commission_eligibility.rule_id}`, {
          rate_amount: payload.rate_amount,
          currency: payload.currency,
          requires_completed_months: payload.requires_completed_months,
          active: payload.active,
        });
      } else {
        await api.post("/tenant/commission-rules", payload);
      }
      toast.success("Commission eligibility updated");
      await load();
    } catch (e) {
      toastError(e);
    } finally {
      setSavingRule(false);
    }
  };

  if (!canView) return null;
  if (!data) return <div className="text-sm text-slate-500">Loading user view…</div>;

  return (
    <section className="border border-slate-200 rounded-lg bg-white" data-testid="user-360-panel">
      <header className="px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-800">{managementOnly ? "User & commission settings" : "User 360"}</h2>
      </header>
      <div className="grid gap-4 p-4 md:grid-cols-2">
        <div className="space-y-2">
          <div className="text-sm font-medium">{data.user.name || "Unnamed user"}</div>
          <div className="text-xs text-slate-500">{data.user.email}</div>
          <div className="text-xs text-slate-500">Role: {data.user.tenant_role || "—"}</div>
        </div>
        <div className="space-y-2 rounded border border-slate-200 p-3">
          <div className="text-sm font-medium">Commission Rule for {data.user.name || "this user"}</div>
          <div className="text-xs text-slate-500">
            {data.commission_eligibility.eligible
              ? `Eligible at ${data.commission_eligibility.rate_amount ?? "—"} ${data.commission_eligibility.currency ?? "CAD"}`
              : "No active commission rule"}
          </div>
          <div className="text-xs text-slate-500">
            Requires {data.commission_eligibility.requires_completed_months ?? 3} completed months
          </div>
          <div className="text-xs text-slate-500">
            Summary: unpaid {data.commission_summary.unpaid}, paid {data.commission_summary.paid}, non-commissionable {data.commission_summary.non_commissionable}
          </div>
        </div>
      </div>

      {canManageCommission && (
        <div className="border-t border-slate-100 p-4" data-testid="commission-rule-editor">
          <div className="mb-1 text-sm font-medium">Commission Rule for {data.user.name || "this user"}</div>
          <p className="mb-3 text-xs text-slate-500">Changes apply from their effective date. Existing commission ledger snapshots remain unchanged.</p>
          <div className="grid gap-3 sm:grid-cols-4">
            <div>
              <Label htmlFor="commission-rate">Amount per converted patient</Label>
              <Input
                id="commission-rate"
                type="number"
                min="0"
                step="0.01"
                value={ruleForm.rate_amount}
                onChange={(event) => setRuleForm({ ...ruleForm, rate_amount: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="commission-currency">Currency</Label>
              <Input
                id="commission-currency"
                maxLength={3}
                value={ruleForm.currency}
                onChange={(event) => setRuleForm({ ...ruleForm, currency: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="commission-months">Starts after completed months</Label>
              <Input
                id="commission-months"
                type="number"
                min="0"
                step="1"
                value={ruleForm.requires_completed_months}
                onChange={(event) => setRuleForm({ ...ruleForm, requires_completed_months: event.target.value })}
              />
            </div>
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 pb-2 text-sm">
                <input
                  type="checkbox"
                  checked={ruleForm.active}
                  onChange={(event) => setRuleForm({ ...ruleForm, active: event.target.checked })}
                />
                Eligible
              </label>
              <Button onClick={saveCommissionRule} disabled={savingRule} data-testid="save-commission-rule">
                {savingRule ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {!managementOnly && <>
      <div className="border-t border-slate-100 p-4 space-y-3" data-testid="user-360-training">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium">Training & policy acknowledgment</div>
          <div className="text-xs text-slate-500">
            Assigned {data.training_summary?.assigned || 0} · Acknowledged {data.training_summary?.acknowledged || 0} · Pending {data.training_summary?.pending || 0}
          </div>
        </div>
        {!data.training_records?.length ? (
          <p className="text-sm text-slate-500">No training materials assigned.</p>
        ) : (
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
                <th className="px-3 py-2 text-left">Material</th><th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Due</th><th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Acknowledged / signature</th>
              </tr></thead>
              <tbody>{data.training_records.map((record) => <tr key={record.material_id} className="border-t border-slate-200">
                <td className="px-3 py-2"><div className="font-medium">{record.title}</div><div className="text-xs text-slate-500">Version {record.material_version} · {record.kind}</div></td>
                <td className="px-3 py-2">{record.category}</td>
                <td className="px-3 py-2">{record.due_at ? new Date(record.due_at).toLocaleString() : "—"}</td>
                <td className={`px-3 py-2 font-medium ${record.status === "acknowledged" ? "text-emerald-700" : "text-amber-700"}`}>{record.status}</td>
                <td className="px-3 py-2">{record.acknowledged_at ? <><div>{new Date(record.acknowledged_at).toLocaleString()}</div><div className="text-xs text-slate-500">Signed: {record.typed_signature || "—"}</div></> : "—"}</td>
              </tr>)}</tbody>
            </table>
          </div>
        )}
      </div>
      <div className="border-t border-slate-100 p-4 space-y-3">
        <div className="text-sm font-medium">Assigned contacts</div>
        {data.assigned_contacts.length === 0 ? (
          <p className="text-sm text-slate-500">No assigned contacts.</p>
        ) : (
          <ul className="space-y-2">{data.assigned_contacts.map((item) => (
            <li key={item.id} className="text-sm text-slate-700">
              {item.first_name} {item.last_name || ""} · {item.lifecycle_status || "—"}
              {item.location ? ` · ${item.location}` : ""}
            </li>
          ))}</ul>
        )}
      </div>
      <div className="border-t border-slate-100 p-4 space-y-3">
        <div className="text-sm font-medium">Converted patients</div>
        {data.converted_patients.length === 0 ? (
          <p className="text-sm text-slate-500">No conversions yet.</p>
        ) : (
          <ul className="space-y-2">{data.converted_patients.map((item) => (
            <li key={item.id} className="text-sm text-slate-700">
              {item.patient_name || "Unnamed patient"} · {item.commission_status}
              {item.rule_snapshot?.rate_amount != null
                ? ` · ${item.rule_snapshot.rate_amount} ${item.rule_snapshot.currency ?? "CAD"}`
                : ""}
            </li>
          ))}</ul>
        )}
      </div>
      </>}
    </section>
  );
}
